import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import MobileCardList, { TableColumn } from '../components/common/MobileCardList';
import { useGrn, useGrnItems } from '../hooks/useGrn';

export default function GrnView() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const grnId = id ? parseInt(id) : null;

  const { data: grnData, isLoading: grnLoading, isError: grnError, error: grnErrorData } = useGrn(grnId);
  const {
    data: itemsData,
    isLoading: itemsLoading,
    isError: itemsError,
    error: itemsErrorData,
  } = useGrnItems(grnId);

  const grn = grnData?.data;
  const items = itemsData?.data || [];
  const isConfirmed = grn?.status === 'confirmed';
  const getErrorMessage = (error: any, fallback: string) => {
    return error?.response?.data?.message || error?.message || fallback;
  };

  // Shared by the desktop table and the mobile card list
  const itemColumns: TableColumn[] = [
    { key: 'productDescription', label: 'Product' },
    { key: 'skuCode', label: 'SKU Code', render: (value) => <span className="font-semibold text-gray-900">{value || '-'}</span> },
    { key: 'itemCode', label: 'Item Code', render: (value) => <span className="text-gray-600">{value || '-'}</span> },
    { key: 'productCategoryName', label: 'Category', render: (value, item) => value || item.productCategory?.name || '-' },
    { key: 'unitType', label: 'Unit' },
    { key: 'goodQty', label: 'Good' },
    { key: 'cdamQty', label: 'C.DAM' },
    { key: 'damQty', label: 'DAM' },
    {
      key: 'actualQty',
      label: 'Actual',
      render: (_, item) => <span className="font-semibold text-gray-900">{item.goodQty + item.cdamQty + item.damQty}</span>,
    },
    {
      key: 'shortExcess',
      label: 'Excess/Short',
      render: (value) => {
        const itemExcess = value || 0;
        const itemExcessStatus = itemExcess > 0 ? { label: 'Excess', color: 'text-orange-600 bg-orange-100' } :
                                itemExcess < 0 ? { label: 'Short', color: 'text-red-600 bg-red-100' } :
                                { label: 'Match', color: 'text-green-600 bg-green-100' };
        return (
          <div className="flex items-center justify-end lg:justify-start space-x-2">
            <span className="font-semibold">{itemExcess}</span>
            <span className={`px-2 py-0.5 text-xs font-semibold rounded ${itemExcessStatus.color}`}>
              {itemExcessStatus.label}
            </span>
          </div>
        );
      },
    },
    { key: 'palletCount', label: 'Pallets', render: (value) => value || '-' },
    { key: 'totalWeight', label: 'Weight', render: (value) => `${value} kg` },
    { key: 'batchNo', label: 'Batch', render: (value) => <span className="text-gray-600">{value || '-'}</span> },
    {
      key: 'expiryDate',
      label: 'Expiry',
      render: (value) => <span className="text-gray-600">{value ? new Date(value).toLocaleDateString() : '-'}</span>,
    },
  ];

  // Loading state
  if (grnLoading || itemsLoading) {
    return (
      <Layout>
        <div className="min-h-screen px-1 py-6 lg:p-6 bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading GRN details...</p>
          </div>
        </div>
      </Layout>
    );
  }

  // Error state
  if (grnError || !grn) {
    return (
      <Layout>
        <div className="min-h-screen px-1 py-6 lg:p-6 bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="text-red-500 text-4xl mb-4">⚠️</div>
            <p className="text-red-600 font-medium">GRN not found</p>
            {grnErrorData && (
              <p className="text-gray-600 text-sm mt-2">
                {getErrorMessage(grnErrorData, 'Unable to load GRN')}
              </p>
            )}
            <button 
              onClick={() => navigate('/grn')} 
              className="mt-4 px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700"
            >
              Back to GRN List
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen px-1 py-6 lg:p-6 bg-gray-50">
        {/* Header */}
        <div className="mb-6 flex flex-wrap justify-between items-center gap-3">
          <div>
            <h2 className="text-3xl font-bold text-gray-900">View GRN</h2>
            <p className="text-gray-600 mt-1">GRN Number: {grn.grnNumber}</p>
          </div>
          <button
            onClick={() => navigate('/grn')}
            className="w-full lg:w-auto px-4 py-2 min-h-[44px] lg:min-h-0 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 whitespace-nowrap"
          >
            ← Back to List
          </button>
        </div>

        {/* Status Badge */}
        <div className="mb-6">
          <span className={`px-4 py-2 inline-flex text-sm font-semibold rounded-full
            ${isConfirmed ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}
          >
            {isConfirmed ? '✓ Confirmed' : 'Draft'}
          </span>
        </div>

        {/* GRN Details */}
        <div className="card mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">GRN Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <p className="text-sm text-gray-600 mb-1">GRN Number</p>
              <p className="text-base font-semibold text-gray-900">{grn.grnNumber}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Client Name</p>
              <p className="text-base font-semibold text-gray-900">{grn.client?.companyName || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Warehouse</p>
              <p className="text-base font-semibold text-gray-900">{grn.warehouse?.name || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Entry Number</p>
              <p className="text-base font-semibold text-gray-900">{grn.gateEntry?.entryNumber || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Vehicle Number</p>
              <p className="text-base font-semibold text-gray-900">{grn.gateEntry?.vehicleNumber || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Dock Number</p>
              <p className="text-base font-semibold text-gray-900">{grn.gateEntry?.dockNumber || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Invoice Number</p>
              <p className="text-base font-semibold text-gray-900">{grn.gateEntry?.invoiceNumber || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Created At</p>
              <p className="text-base font-semibold text-gray-900">
                {new Date(grn.createdAt).toLocaleString()}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Created By</p>
              <p className="text-base font-semibold text-gray-900">{grn.createdByUser?.name || '-'}</p>
            </div>
          </div>
        </div>

        {/* Items List */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Items ({items.length})
          </h3>

          {itemsError ? (
            <div className="text-center py-8">
              <p className="text-red-600 text-sm">
                {getErrorMessage(itemsErrorData, 'Failed to load GRN items')}
              </p>
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-12">
              <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
              <p className="text-gray-600">No items in this GRN</p>
            </div>
          ) : (
            <>
            <MobileCardList columns={itemColumns} data={items} />
            <div className="hidden lg:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">SKU Code</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Item Code</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Unit</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Good</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">C.DAM</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">DAM</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actual</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Excess/Short</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pallets</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Weight</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Batch</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expiry</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {items.map((item: any) => {
                    const itemActualQty = item.goodQty + item.cdamQty + item.damQty;
                    const itemExcess = item.shortExcess || 0;
                    const itemExcessStatus = itemExcess > 0 ? { label: 'Excess', color: 'text-orange-600 bg-orange-100' } :
                                            itemExcess < 0 ? { label: 'Short', color: 'text-red-600 bg-red-100' } :
                                            { label: 'Match', color: 'text-green-600 bg-green-100' };
                    
                    return (
                      <tr key={item.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900">{item.productDescription}</td>
                        <td className="px-4 py-3 text-sm font-semibold text-gray-900">{item.skuCode || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{item.itemCode || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{item.productCategoryName || item.productCategory?.name || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{item.unitType}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{item.goodQty}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{item.cdamQty}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{item.damQty}</td>
                        <td className="px-4 py-3 text-sm font-semibold text-gray-900">{itemActualQty}</td>
                        <td className="px-4 py-3 text-sm">
                          <div className="flex items-center space-x-2">
                            <span className="font-semibold">{itemExcess}</span>
                            <span className={`px-2 py-0.5 text-xs font-semibold rounded ${itemExcessStatus.color}`}>
                              {itemExcessStatus.label}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">{item.palletCount || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{item.totalWeight} kg</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{item.batchNo || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {item.expiryDate ? new Date(item.expiryDate).toLocaleDateString() : '-'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}
